import { useCallback, useEffect, useState } from "react";
import { DemoAirline } from "../config/movmoIdentity";
import { getMeWithCachedSession } from "../services/demoAirlineApi";
import {
    LinkedTraveler,
    MovmoTraveler,
    toMovmoTraveler,
    useLinkedTravelerState,
} from "./linkedTravelerStore";

/**
 * Flow B: reads the traveler's linked Movmo identity from the AIRLINE's own
 * session (GET /me), not from a Movmo surface. A browser that never completed
 * the chip's OAuth round trip has no cached session, so nothing is requested
 * and the traveler is unlinked.
 *
 * `checked` flips true once the session read has settled (success, no
 * session, or failure). Callers hold the chip back until then so a linked
 * traveler never sees it flash. On an upstream failure the stored traveler is
 * left as it was: an outage must not look like a revocation.
 */
export function useLinkedTraveler(airline: DemoAirline): {
    traveler: LinkedTraveler | null;
    /** The chip's `traveler` prop: undefined while pending, null anonymous, `{ firstName }` linked. */
    movmoTraveler: MovmoTraveler;
    checked: boolean;
    /** Re-reads GET /me and updates the store (used after a recognition link). */
    refresh: () => Promise<void>;
} {
    const { traveler, setTraveler, clearTraveler } = useLinkedTravelerState(airline);
    const [checked, setChecked] = useState(false);

    const refresh = useCallback(async () => {
        const me = await getMeWithCachedSession(airline);
        if (me?.movmo.recognized) {
            setTraveler({
                display_name: me.traveler?.display_name || undefined,
                profile: me.movmo.profile ?? undefined,
                preferences: me.movmo.preferences ?? undefined,
                loyalty: me.movmo.loyalty ?? undefined,
            });
        } else {
            clearTraveler();
        }
    }, [airline, setTraveler, clearTraveler]);

    useEffect(() => {
        let cancelled = false;
        getMeWithCachedSession(airline)
            .then((me) => {
                if (cancelled) return;
                if (me?.movmo.recognized) {
                    setTraveler({
                        display_name: me.traveler?.display_name || undefined,
                        profile: me.movmo.profile ?? undefined,
                        preferences: me.movmo.preferences ?? undefined,
                        loyalty: me.movmo.loyalty ?? undefined,
                    });
                } else {
                    clearTraveler();
                }
            })
            .catch((err) => {
                console.error(`[${airline}] airline session read failed:`, err);
            })
            .finally(() => {
                if (!cancelled) setChecked(true);
            });
        return () => {
            cancelled = true;
        };
    }, [airline, setTraveler, clearTraveler]);

    return { traveler, movmoTraveler: toMovmoTraveler(traveler, checked), checked, refresh };
}
